import { UIIcon } from '../../../config/icons';
import { sUploadImage } from '../../mdl/s-upload';
import { meGetPreference, meSetPreference } from '../../me/preferences';
import { $on, $trigger, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput, YInputOptions } from '../../y/form';
import { yClamp } from '../../y/math';
import { YId, yIsValidId } from '../../y/types';
import { onDetach } from '../attach';
import { uiSetBG } from '../bg';
import { $color, UIColor } from '../color';
import { isImageMime, UIContent, UIDropDelegate, uiInitDropTarget, uiStartDrag } from '../dnd';
import { $el } from '../elements';
import { uiGetElements, uiMakeFakeUploadInput, uiPreloadImage } from '../functions';
import { uiShowPopup } from '../popup';
import { onInteract, onTouch, UITouchEvent } from '../touch';
import { UIRelPos } from '../types';
import { UIMultiViewSpec, UIView } from '../view';
import { UIButton } from './button';
import { UITextBasedInput, UITextBasedInputOptions } from './input-base';

let selected_color_sink: UITextColorSink | undefined;

export class UIColorDrop extends UIView {
    constructor(color: string) {
        super('div', 'ui-color-drop');
        this.root.style.background = color;
    }
}

export class UIPaletteBox extends UIView {
    private _value!: string | null;

    constructor(horizontal?: boolean) {
        super('div', 'ui-palette-box');

        this.root.addEventListener('click', () => {
            if(this._value && selected_color_sink)
                selected_color_sink.changeValue(this._value);
            const picker = new UIColorPicker($color.make(this._value || '#000'), { input: true });
            $on(picker, 'value', color => {
                this.value = $color.HEX(color);
            });
            uiShowPopup(picker, this, { position: horizontal ? UIRelPos.Below | UIRelPos.Center : UIRelPos.Left | UIRelPos.Start, offset: 6, close_on_click: true });
        });

        onInteract(this, e => {
            const value = this._value;

            if(value)
                return uiStartDrag(e, { dragStart: () => [{ type: 'color', value: value }, new UIColorDrop(value)] });

            return false;
        });
    }

    set value(c: string | null) {
        this._value = c;
        if(c) {
            this.classList.toggle('m-light', $color.isVeryLight($color.make(c)));
            this.root.style.background = c;
            this.classList.remove('f-empty');
        } else {
            this.classList.add('f-empty');
        }
    }

    get value() {
        return this._value;
    }
}

interface UIPaletteOptions {
    horizontal?: boolean;
    small?: boolean;
    on_picture?: () => void;
}

export class UIPalette extends UIView {
    private _boxes: UIPaletteBox[];

    constructor(options: UIPaletteOptions = {}) {
        super('div', 'ui-palette');
        this.classList.add('x-drop');

        const palette: (string | null)[] = meGetPreference('ui_palette', ['#000000', '#434561', '#a5d39e', '#ffd460', '#ea5455', '#713045', '#f8f8f8', null]);

        if(options.horizontal)
            this.classList.add('horizontal');
        if(options.small)
            this.classList.add('m-small');

        if(options.on_picture) {
            this.ui_add(new UIButton({ icon: UIIcon.IMAGE, on_click: options.on_picture }));
        }

        this._boxes = [];
        for(const c of palette) {
            const box = new UIPaletteBox(options.horizontal);
            box.value = c;
            this.ui_add(box);
            this._boxes.push(box);
        }

        onDetach(this, () => {
            if(this._boxes.length == 8) {
                const palette = this._boxes.map((b) => b.value);
                meSetPreference('ui_palette', palette);
            }
        });
    }
}

interface UIColorPickerOptions {
    input?: boolean;
}

class UIColorPicker extends UIView {
    $handlers: YEventHandlers<YInputEvent & YValueEvent<UIColor>>;

    private _color!: UIColor;
    private _input?: UIColorInput;
    private _current?: HTMLElement;

    private _square: HTMLElement;
    private _square_over: HTMLElement;
    private _square_marker: HTMLElement;
    private _linear: HTMLElement;
    private _linear_marker: HTMLElement;

    private _mode: 'h' | 's' | 'v';

    private _hex?: string;

    constructor(color: UIColor, options: UIColorPickerOptions = {}) {
        super('div', 'ui-color-picker');

        this._mode = 'h';

        const square = this._square = $el.DIV('r-square', [this._square_over = $el.DIV('r-over'), this._square_marker = $el.DIV('r-marker')]);
        const linear = this._linear = $el.DIV('r-linear', this._linear_marker = $el.DIV('r-marker'));

        this.ui_add($el.DIV('r-top', [square, linear]));

        let setSquare = (e: UITouchEvent) => {
            const hsv = $color.HSV(this._color, true);
            const rect = square.getBoundingClientRect();
            const h = yClamp((e.touch.x - rect.left) / square.offsetWidth, 0, 1);
            const v = yClamp((e.touch.y - rect.top) / square.offsetHeight, 0, 1);

            if(this._mode == 'h') {
                hsv[1] = h;
                hsv[2] = 1 - v;
            } else if(this._mode == 's') {
                hsv[0] = h * 360;
                hsv[2] = 1 - v;
            } else {
                hsv[0] = h * 360;
                hsv[1] = 1 - v;
            }

            this._doSetValue(hsv, false);

            return true;
        }

        let setLinear = (e: UITouchEvent) => {
            const hsv = $color.HSV(this._color, true);
            const rect = linear.getBoundingClientRect();
            const l = yClamp((e.touch.y - rect.top) / square.offsetHeight, 0, 1);

            if(this._mode == 'h') {
                hsv[0] = l * 360;
            } else if(this._mode == 's') {
                hsv[1] = 1 - l;
            } else {
                hsv[2] = 1 - l;
            }

            this._doSetValue(hsv, false);

            return true;
        }

        onTouch(square, e => {
            setSquare(e);
            return { touchMove: setSquare, stop: true };
        });
        onTouch(linear, e => {
            setLinear(e);
            return { touchMove: setLinear, stop: true };
        });

        if(options.input) {
            const input = this._input = new UIColorInput({ kind: 'k-flat', no_box: true });

            this.ui_add($el.DIV('r-bottom', [
                $el.S(),
                input,
                $el.S(),
                this._current = $el.DIV('r-current')
            ]));

            $on(input, 'value', v => {
                let color = $color.maybe(v);
                if(color)
                    this._doSetValue(color, true);
            });
        }

        this._doSetValue(color, false);
    }

    private _setMarkers(l: number, h: number, v: number) {
        this._linear_marker.style.top = (l * 178 - 4) + 'px';
        this._square_marker.style.left = (h * 178 - 4) + 'px';
        this._square_marker.style.top = (v * 178 - 4) + 'px';
    }

    private _doSetValue(color: UIColor, from_input: boolean) {
        this._color = color;

        const hsv = $color.HSV(color, true);

        if(this._mode == 'h') {
            this._setMarkers(hsv[0] / 360, hsv[1], 1 - hsv[2]);

            hsv[1] = 1;
            hsv[2] = 1;

            this._square.style.backgroundImage = 'linear-gradient(to right,white,' + $color.HEX(hsv) + ')';
            this._square_over.style.backgroundImage = 'linear-gradient(to bottom,rgba(0,0,0,0),black)';
            this._linear.style.backgroundImage = 'linear-gradient(to bottom, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)';
        } else if(this._mode == 's') {
            this._setMarkers(1 - hsv[1], hsv[0] / 360, 1 - hsv[2]);
            const sat = hsv[1];
            hsv[1] = 1;
            const t = $color.HEX(hsv);
            hsv[1] = 0;
            const b = $color.HEX(hsv);
            this._linear.style.background = 'linear-gradient(to bottom,' + t + ',' + b + ')';

            hsv[1] = sat;
            hsv[2] = 1
            let back = 'linear-gradient(to right,';
            hsv[0] = 0;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 60;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 120;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 180;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 240;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 300;
            back += $color.HEX(hsv) + ',';
            hsv[0] = 360;
            back += $color.HEX(hsv) + ')';

            this._square.style.backgroundImage = back;
            this._square_over.style.backgroundImage = 'linear-gradient(to bottom,rgba(0,0,0,0),rgba(0,0,0,1)';
        } else {
            this._setMarkers(1 - hsv[2], hsv[0] / 360, 1 - hsv[1]);

            this._square.style.backgroundImage = 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)';
            const v = Math.round(255 * hsv[2]);
            const a = (1 - hsv[2]).toFixed(2);
            this._square_over.style.backgroundImage = 'linear-gradient(to bottom,rgba(0,0,0,' + a + '),rgba(' + v + ',' + v + ',' + v + ',1)';
            hsv[2] = 1;
            this._linear.style.backgroundImage = 'linear-gradient(to bottom,' + $color.HEX(hsv) + ',black)'
        }

        const hex = $color.HEX(color);
        if(hex === this._hex) return;
        this._hex = hex;
        if(this._input && !from_input) {
            this._input.value = hex;
            this._current!.style.background = hex;
        }
        $trigger(this, 'value', color);
    }

    set value(color: UIColor) {
        this._doSetValue(color, false);
    }

    get value() {
        return this._color;
    }
}

interface UIBackgroundSinkOptions extends YInputOptions {
    value?: string;
    image?: boolean;
    default_color?: string;
    scope: string;
}

export class UIBackgroundSink implements YInput<string | undefined>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<string | undefined>>;

    private _element?: HTMLElement;
    private _value?: string;
    private _image: boolean;
    private _scope: string;
    private _default_color?: string;

    private _preview?: HTMLElement;
    private _pending_preview?: string;
    private _input?: HTMLInputElement;

    okay: boolean;
    dirty!: boolean;
    disabled!: boolean;

    constructor(view: UIView | undefined, options: UIBackgroundSinkOptions) {
        this._image = !!options.image;
        if(view)
            this.attachTo(view);
        this._default_color = options.default_color;
        this._scope = options.scope;
        this.value = options.value;
        this.okay = true;
    }

    openPicker() {
        if(!this._input) {
            this._input = uiMakeFakeUploadInput((files) => {
                this._onSetImage(files[0]);
            });
        }

        this._input.click();
    }

    attachTo(view: UIView) {
        uiInitDropTarget(view, this);
        this._element = view.ui_elem;
        view.classList.add('ui-background-sink');

        if(this._image) {
            view.addHelperView(new UIButton({
                icon: UIIcon.UPLOAD, kind: 'k-square', classes: 'r-sink-upload', on_click: () => {
                    this.openPicker();
                }
            }));
        }
    }

    dropEnter(content: UIContent) {
        return content.type === 'color' || content.type === 'files';
    }

    drop(content: UIContent) {
        if(content.type === 'color') {
            $form.setDirty(this, true);
            this.value = content.value || undefined;
        } else if(content.type === 'files' && this._image) {
            const file = content.value![0] as File
            if(isImageMime(file.type))
                this._onSetImage(file);
        }
    }

    get value() {
        return this._value;
    }

    set value(v: string | undefined) {
        this._value = v;
        const elem = this._element;
        if(elem)
            uiSetBG(elem, !v ? { color: this._default_color || '' } : yIsValidId(v) ? { media: v } : { color: v });

        $trigger(this, 'value', v);
    }

    preview(image: YId | undefined) {
        if(!this._element) return;

        this._pending_preview = image;

        if(!image) {
            if(this._preview) {
                this._element.removeChild(this._preview);
                this._preview = undefined;
            }
        } else {
            if(!this._preview) {
                this._preview = $el.DIV('r-sink-preview');
                this._element.insertBefore(this._preview, this._element.firstChild);
            }

            uiPreloadImage(image).then(() => {
                if(this._pending_preview === image)
                    uiSetBG(this._preview!, { media: image });
            },
                () => {
                    if(this._pending_preview === image) {
                        this._element!.removeChild(this._preview!);
                        this._preview = undefined;
                    }
                });
        }
    }

    _onSetImage(file: File) {
        if(this._element) {
            $form.setDirty(this, true);
            // TODO: release the object URL when done with it
            const url = URL.createObjectURL(file);
            uiSetBG(this._element!, { url: url });
            sUploadImage(file, this._scope, {}, () => {
            }).then(img => {
                this._value = img.id;
                $trigger(this, 'value', img.id);
            });
        }
    }

    markInvalid(_focus: boolean, _force?: boolean) {
        return false;
    }

    focus() {

    }
}

interface UITextColorSinkOptions extends YInputOptions {
    value?: string;
    whole_box?: boolean;
}

class UITextColorSink implements YInput<string | undefined>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<string | undefined>>;

    private _elements: HTMLElement[];

    okay: boolean;
    dirty!: boolean;
    disabled!: boolean;

    private _value?: string;
    private _whole_box?: boolean;


    constructor(what?: UIMultiViewSpec, options: UITextColorSinkOptions = {}) {
        this._elements = [];

        if(what)
            this.attachTo(what);
        if(options.value)
            this.value = options.value;
        this._whole_box = options.whole_box;

        this.okay = true;
    }

    attachTo(what: UIMultiViewSpec) {
        const els = uiGetElements(what);

        for(const el of els) {
            uiInitDropTarget(el, this);
            el.ui_drop_text_only = !this._whole_box;
            this._elements.push(el);
            if(this._value)
                el.style.color = this._value;
            el.addEventListener('focus', () => {
                selected_color_sink = this;
            });
            el.addEventListener('blur', () => {
                if(selected_color_sink === this)
                    selected_color_sink = undefined;
            });
        }
    }

    dropEnter(content: UIContent) {
        if(content.type === 'color') {
            for(const el of this._elements) {
                el.classList.add('ui-text-highlight');
            }

            return true;
        }

        return false;
    }

    dropLeave() {
        for(const el of this._elements) {
            el.classList.remove('ui-text-highlight');
        }
    }

    drop(content: UIContent) {
        for(const el of this._elements) {
            el.classList.remove('ui-text-highlight');
        }

        if(content.type === 'color') {
            this.changeValue(content.value!);
        }
    }

    changeValue(v: string) {
        $form.setDirty(this, true);
        this.value = v;
        $trigger(this, 'value', v);
    }

    get value() {
        return this._value;
    }

    set value(v: string | undefined) {
        this._value = v;
        if(v) {
            for(const el of this._elements) {
                el.style.color = v;
            }
        }
    }

    markInvalid(_focus: boolean, _force?: boolean) {
        return false;
    }

    focus() {

    }
}

export interface UIColorInputOptions extends UITextBasedInputOptions<string> {
    no_box?: boolean;
}

export class UIColorInput extends UITextBasedInput<string | undefined> {
    private _value?: string;
    private _box?: HTMLElement;

    constructor(options: UIColorInputOptions) {
        const color_non_digit = /[^0-9A-Fa-f]/g;

        let box: HTMLElement | undefined;
        if(!options.no_box) {
            options.l_deco = box = $el.DIV('r-color');

            box.addEventListener('click', () => {
                const picker = new UIColorPicker($color.make(this._value || '#000'));
                $on(picker, 'value', color => { this.value = $color.HEX(color) });
                uiShowPopup(picker, this, { position: UIRelPos.Below | UIRelPos.Start, close_on_click: true, can_flip: true });
            });
        }

        options.fixer = (text, pos) => {
            if(!text) return;
            pos = text.substring(0, pos).replace(color_non_digit, '').length;
            text = ('#' + text.replace(color_non_digit, '')).substr(0, 7);

            return [text, Math.min(pos + 1, text.length)];
        }

        if(window.EyeDropper) {
            const eye_dropper = new window.EyeDropper;
            const button = $el.ICON(UIIcon.WAND_MAGIC_SPARKLES, 'u-clickable');
            options.r_deco = button;
            button.addEventListener('click', () => {
                eye_dropper.open().then(color => { this.value = color.sRGBHex });
            });
        }

        super(options);
        this.classList.add('ui-color-input')

        this._box = box;

        this.doCheck();
    }

    get value() {
        return this._value;
    }

    set value(c: string | undefined) {
        this.setText(c || '', false);
    }

    onText(text: string, notify: boolean) {
        this._value = text || undefined;
        if(this._box)
            this._box.style.background = this._value || '#000';
        this.fixDesign();
        this.doCheck();
        $trigger(this, 'value', this._value);
        if(notify)
            $trigger(this, 'input');
    }

    protected doCheck() {
        this.setOkay(!(this.required && !this._value))
    }
}
